/**
 * Maintenance module design tokens.
 *
 * BUTTON COLOUR CONVENTION (Session 78 — added after button colours
 * drifted across the client surfaces). There are exactly three button
 * shapes on a maintenance surface, and no others without a decision:
 *
 *   PRIMARY   ink fill (#1a1a1a), white text, 1px ink border.
 *             The one action the surface is FOR. At most one per group.
 *   SECONDARY cardBg fill, 1px `divider` border, ink text.
 *             Everything else that is still a button.
 *   TEXT LINK `link` (#185FA5), underlined, no fill, no border.
 *
 * `link` IS A TEXT COLOUR. It is named for the mockups' `.link` class
 * and must never be a button FILL or a button border — a blue filled
 * button is not in the mockups and reads as a second, competing primary
 * next to the ink one. Status hues (`green`, `teal`, `amber`, `red`)
 * are for PILLS AND TEXT only: a teal or amber button invents a
 * priority level the rest of the app does not have.
 *
 * If an action genuinely needs to outrank the primary, it gets the ink
 * card treatment (mockup 25), not a new colour.
 *
 * WHY THIS EXISTS (Session 54 fidelity pass): the maintenance mockups
 * (docs/maintenance-module/mockups/*.svg) use a specific, consistent
 * visual system that does NOT match the global theme palette
 * (themevals.ts). For example the mockups' "completed/on-track" green is
 * #27500A, but the global theme's `success` is #667E4E (olive); the
 * mockups' status-pill fills (#EAF3DE / #E1F5EE / #FFF8E1 / #FCEBEB) are
 * not in the global badge set. So every maintenance surface was
 * hand-coding these hex values inline to approximate the mockups, and
 * each did it slightly differently or reached for a theme-ui default and
 * drifted. That inline, per-component styling is the root cause of "it
 * doesn't look like the mockups even where the structure matches."
 *
 * This module is the SINGLE SOURCE for the mockups' visual language.
 * Maintenance surfaces import from here instead of hand-coding hex, so
 * the palette, type scale, card recipes, and pill recipes are identical
 * everywhere and match the mockups by construction.
 *
 * SCOPE: maintenance surfaces only. This deliberately does NOT touch the
 * global theme (themevals.ts) — ~284 clients and the whole trunk depend
 * on that, and the maintenance look is intentionally its own within the
 * app. Values below are read directly off the mockup SVGs, not invented.
 */

/* ── Palette — every value is lifted verbatim from a mockup SVG ── */
export const mnt = {
  // Ink + text
  ink: "#1a1a1a", // headings, primary text (mockup .h1/.h2/.body-txt)
  textMuted: "#5f5e5a", // labels, secondary text (mockup .label/.body-muted)
  textFaint: "#888780", // breadcrumb, disabled (mockup .breadcrumb)
  link: "#185FA5", // links + active-provider (mockup .link)

  // Surfaces
  bgBeige: "#F1EFE8", // stat cards, context banners, inline hint strips (.panel/.stat-card)
  bgSubtle: "#FAFAF8", // table header rows, hover, quiet callout bg
  cardBg: "#FFFFFF", // content cards (mockup .card)
  fieldBg: "#F7F5EE", // copy-field boxes in the corrigo export (.field-box)

  // Hairlines
  divider: "#D3D1C7", // 0.5px rules and card strokes everywhere
  stepTrack: "#E2DFD4", // mockup 25: not-yet-reached stage connector

  // On-dark (mockup 25's guided next-action card is ink-filled)
  onInk: "#FFFFFF", // title text on the ink card
  onInkMuted: "#C9C7C0", // body text on the ink card

  // Status — foreground / background pairs, one hue per state.
  // Foregrounds are the dark text color, backgrounds the pill fill.
  green: "#27500A", // completed / on-track / progress-bar fill
  greenPill: "#EAF3DE", // "Current" / "On track" pill bg
  greenFg: "#27500A",
  teal: "#085041", // scheduled / WO-received / at-NTE
  tealPill: "#E1F5EE",
  amber: "#7A5A00", // due-soon / warning text (darker than #633806 for AA on beige)
  amberDeep: "#633806",
  amberPill: "#FFF8E1",
  amberStroke: "#E7CC7A", // amber callout/card border
  red: "#791F1F", // overdue / over-NTE
  redPill: "#FCEBEB",
  grey: "#5f5e5a", // ungrouped / closed / neutral pill fg
  greyPill: "#F1EFE8",
  sourcePill: "#EDE9F6", // CorrigoPro source chip bg (soft lavender)
  sourcePillFg: "#4B3F72",
  // Mockup 06 draws the queue's CorrigoPro source chip in soft blue with
  // the link ink, and the Manual chip in the grey pair. Kept separate
  // from `sourcePill` above (used by the Corrigo export panel) so the
  // two surfaces don't fight over one value.
  corrigoPill: "#E3ECF7",
  corrigoPillFg: "#185FA5",
} as const;

/* ── Type scale — the mockups' exact sizes/weights/colors ──
 * Use as sx spreads: <Box sx={mntType.valueLg}>132</Box> */
export const mntType = {
  // 20px / 500 — the big stat-card number (mockup .value-lg). NOTE the
  // built StatCards drifted to 22/600; this is the mockup's real value.
  valueLg: { fontSize: "20px", fontWeight: 500, color: mnt.ink, fontFamily: "Poppins" },
  // 13px / 500 — section headings (mockup .h2)
  h2: { fontSize: "13px", fontWeight: 500, color: mnt.ink, fontFamily: "Poppins" },
  // 13px — body text
  body: { fontSize: "13px", color: mnt.ink, fontFamily: "Poppins" },
  // 12px — muted body / sub-lines
  bodyMuted: { fontSize: "12px", color: mnt.textMuted, fontFamily: "Poppins" },
  // 11px — labels, table headers (mockup .label-sm). Uppercased at call
  // site where the mockup shows caps (stat-card + table headers).
  labelSm: { fontSize: "11px", color: mnt.textMuted, fontFamily: "Poppins" },
} as const;

/* ── Recipes — spread into sx to get a mockup-exact component ── */

/**
 * Stat card. Mockup 12/13: borderless, soft beige, rounded 8px. The
 * single highest-payoff fix — the built cards are white-with-border,
 * which is most of why the pages feel colder than the mockups.
 * Pass tone:"warning" ONLY where the mockup itself tints a card amber
 * (rare; the mockups keep stat cards calm and put urgency in callouts).
 */
export const mntStatCard = (tone?: "warning") => ({
  background: tone === "warning" ? mnt.amberPill : mnt.bgBeige,
  border: tone === "warning" ? `1px solid ${mnt.amberStroke}` : "none",
  borderRadius: "8px",
  padding: "16px 18px",
});

/** Content card (white, hairline stroke). Mockup .card. */
export const mntCard = {
  background: mnt.cardBg,
  border: `0.5px solid ${mnt.divider}`,
  borderRadius: "8px",
};

/** Hairline divider as a border value. */
export const mntDividerBorder = `0.5px solid ${mnt.divider}`;

/** Status pill. Returns an sx block; children are the label text. */
export type MntStatus =
  | "current"
  | "onTrack"
  | "scheduled"
  | "woReceived"
  | "atNte"
  | "due"
  | "upcoming"
  | "overdue"
  | "urgent"
  | "overNte"
  | "ungrouped"
  | "closed";

const PILL: Record<MntStatus, { bg: string; fg: string }> = {
  current: { bg: mnt.greenPill, fg: mnt.greenFg },
  onTrack: { bg: mnt.greenPill, fg: mnt.greenFg },
  scheduled: { bg: mnt.tealPill, fg: mnt.teal },
  woReceived: { bg: mnt.tealPill, fg: mnt.teal },
  atNte: { bg: mnt.tealPill, fg: mnt.teal },
  due: { bg: mnt.amberPill, fg: mnt.amber },
  upcoming: { bg: mnt.greyPill, fg: mnt.grey },
  overdue: { bg: mnt.redPill, fg: mnt.red },
  urgent: { bg: mnt.redPill, fg: mnt.red },
  overNte: { bg: mnt.redPill, fg: mnt.red },
  ungrouped: { bg: mnt.greyPill, fg: mnt.grey },
  closed: { bg: mnt.greyPill, fg: mnt.grey },
};

export const mntPill = (status: MntStatus) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: PILL[status].bg,
  color: PILL[status].fg,
  borderRadius: "9px",
  padding: "3px 10px",
  fontSize: "11px",
  fontFamily: "Poppins",
  fontWeight: 500,
  whiteSpace: "nowrap" as const,
});

/** Progress bar (slim, beige track, green fill). Mockup 12/13. */
export const mntProgressTrack = {
  height: "8px",
  borderRadius: "4px",
  background: mnt.bgBeige,
  overflow: "hidden" as const,
};
export const mntProgressFill = (pct: number, open = true) => ({
  height: "100%",
  width: `${Math.max(0, Math.min(100, pct))}%`,
  background: open ? mnt.green : mnt.textMuted,
  transition: "width 200ms ease",
});

/**
 * mockup 34 (Session 58) — per-step gradient shade for a milestone's
 * progress bar + its expand-ladder dots. Within one milestone, each
 * sub-step reads a progressively DARKER shade left-to-right, so the fill
 * shows depth of progress within the record and the ladder dot next to a
 * step matches its slice of the bar.
 *
 * `base` is the milestone's base hex (mnt.green complete, mnt.link live).
 * `index` is the step's 0-based position, `count` the total steps. Step 0
 * is the lightest tint; the last step is the full base color. Derived by
 * mixing the base toward white by a factor that shrinks as index rises,
 * so no new palette hex is introduced — the ramp is computed from the one
 * base token. A single-step ladder (count<=1) returns the base unmixed.
 */
export const mntStepShade = (base: string, index: number, count: number): string => {
  const hex = base.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Lightest step mixes ~55% toward white; the last step is 0% (full base).
  const maxLighten = 0.55;
  const t =
    count <= 1 ? 0 : maxLighten * (1 - index / (count - 1));
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  const to2 = (n: number) => n.toString(16).padStart(2, "0");
  return `#${to2(mix(r))}${to2(mix(g))}${to2(mix(b))}`;
};
